import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Linkedin, Twitter, ArrowRight, ChevronRight } from 'lucide-react';
import { useCmsTheme } from '@/context/CmsThemeContext';
import { useSiteContent } from '@/context/SiteContentContext';
import { useIntegrationSettings } from '@/hooks/useIntegrationSettings';
import { NewsletterForm } from '@/components/forms/NewsletterForm';
import DynamicIcon from '@/components/common/DynamicIcon';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import hydraLogo from '@/assets/hydra-logo.png';
import ciWebGroupLogo from '@/assets/ci-web-group-logo-horizontal-white-rgb.svg';

const BrandingBar: React.FC = () => (
  <div className="bg-black border-t border-white/10">
    <div className="container mx-auto px-4 py-5">
      <div className="flex items-center justify-between gap-4">
        <a href="https://getstarted.ciwebgroup.com" target="_blank" rel="noopener noreferrer"
          className="group relative px-4 py-2 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 text-white text-xs sm:text-sm font-bold rounded-lg overflow-hidden transition-all duration-300 hover:shadow-[0_0_20px_rgba(59,130,246,0.5)] hover:scale-105 whitespace-nowrap flex-shrink-0">
          <span className="relative z-10 flex items-center gap-2">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="hidden sm:inline">Want a website like this?</span>
            <span className="sm:hidden">Get a Website</span>
          </span>
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-blue-500 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </a>
        <div className="flex items-center gap-3 sm:gap-4 ml-auto">
          <div className="flex items-center gap-2">
            <span className="text-white/50 text-xs whitespace-nowrap hidden md:inline">Powered by</span>
            <a href="https://ciwebgroup.com" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
              <img src={ciWebGroupLogo} alt="AI Website and AI Marketing Agency | Hydra Operating System" className="h-4 sm:h-5 w-auto" />
            </a>
          </div>
          <div className="w-px h-6 sm:h-8 bg-white/20" />
          <img src={hydraLogo} alt="Hydra OS" className="h-8 sm:h-10 w-auto flex-shrink-0" />
        </div>
      </div>
    </div>
  </div>
);

/** Renders all active business locations with Google Maps links */
const LocationsList: React.FC<{ locations: any[]; bizSettings?: Record<string, any> | null; compact?: boolean }> = ({ locations, bizSettings, compact }) => {
  // Fallback to global address if no locations exist
  const items = locations.length > 0
    ? locations.map(loc => ({
        name: loc.name,
        address: [loc.address_street, loc.address_city, loc.address_state, loc.address_zip].filter(Boolean).join(', '),
        googleUrl: loc.google_url,
        phone: loc.phone,
      }))
    : bizSettings
      ? [{
          name: bizSettings.company_name || 'Main Office',
          address: [bizSettings.address_street, bizSettings.address_city, bizSettings.address_state, bizSettings.address_zip].filter(Boolean).join(', '),
          googleUrl: null,
          phone: bizSettings.phone,
        }]
      : [];

  if (items.length === 0) return null;

  return (
    <div className={compact ? "space-y-2" : "space-y-3"}>
      {items.map((loc, i) => (
        <div key={i} className="text-sm text-secondary-foreground/80">
          {items.length > 1 && <p className="font-semibold text-secondary-foreground mb-0.5">{loc.name}</p>}
          {loc.googleUrl ? (
            <a href={loc.googleUrl} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 hover:text-primary transition-colors">
              <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{loc.address}</span>
            </a>
          ) : (
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{loc.address}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const SocialLinks: React.FC<{ bizSettings?: Record<string, any> | null }> = ({ bizSettings }) => {
  const socialLinks = [
    { name: 'Facebook', Icon: Facebook, url: bizSettings?.facebook_url },
    { name: 'Instagram', Icon: Instagram, url: bizSettings?.instagram_url },
    { name: 'LinkedIn', Icon: Linkedin, url: bizSettings?.linkedin_url },
    { name: 'Twitter', Icon: Twitter, url: bizSettings?.twitter_url },
  ].filter(s => s.url);

  if (socialLinks.length === 0) return null;

  return (
    <div className="flex items-center gap-4">
      {socialLinks.map(({ name, Icon, url }) => (
        <a key={name} href={url!} target="_blank" rel="noopener noreferrer" className="text-secondary-foreground hover:text-primary transition-colors" aria-label={name}>
          <Icon className="h-5 w-5" />
        </a>
      ))}
    </div>
  );
};

/**
 * CMS-aware footer with style variants based on cms_theme_config.footer_style
 */
export const CmsThemedFooter: React.FC = () => {
  const { config } = useCmsTheme();
  const ctx = useSiteContent();
  const { settings: integrationSettings } = useIntegrationSettings();
  const currentYear = new Date().getFullYear();


  const { data: bizSettings } = useQuery({
    queryKey: ['business-settings-header'],
    queryFn: async () => {
      const { data } = await pmsFrom('business_settings')
        .select('logo_url, company_name, phone, email, address_street, address_city, address_state, address_zip, business_hours, facebook_url, instagram_url, linkedin_url, twitter_url')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
    staleTime: 1000 * 60 * 10,
  });

  const { data: locations = [] } = useQuery({
    queryKey: ['business-locations-footer'],
    queryFn: async () => {
      const { data } = await pmsFrom('business_locations')
        .select('name, address_street, address_city, address_state, address_zip, google_url, phone, slug, is_active, sort_order')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      return data || [];
    },
    staleTime: 1000 * 60 * 10,
  });

  const logoUrl = bizSettings?.logo_url || null;
  const companyName = bizSettings?.company_name || ctx.template.brand?.name || 'Company';
  const phone = bizSettings?.phone || ctx.template.contact?.phone || '';
  const email = bizSettings?.email || ctx.template.contact?.email || '';
  const address = bizSettings?.address_street || ctx.template.contact?.address || '';
  const city = bizSettings?.address_city || ctx.template.contact?.city || '';
  const state = bizSettings?.address_state || ctx.template.contact?.state || '';
  const zip = bizSettings?.address_zip || ctx.template.contact?.zip || '';
  const hours = bizSettings?.business_hours || ctx.template.contact?.hours || '';

  switch (config.footer_style) {
    case 'minimal':
      return (
        <footer className="bg-secondary text-secondary-foreground">
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-secondary-foreground">© {currentYear} {companyName}. All rights reserved.</p>
              <SocialLinks bizSettings={bizSettings} />
            </div>
          </div>
          <BrandingBar />
        </footer>
      );

    case 'compact':
      return (
        <footer className="bg-secondary text-secondary-foreground">
          <div className="container mx-auto px-4 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-3" style={{ fontFamily: 'var(--cms-heading-font)' }}>
                {logoUrl ? (
                  <img src={logoUrl} alt={companyName} className="h-8 w-auto object-contain" />
                ) : (
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ background: 'var(--cms-accent)' }}>
                    {companyName.charAt(0)}
                  </div>
                )}
                <span className="font-bold">{companyName}</span>
              </div>
              <nav className="flex flex-wrap items-center gap-6 text-sm">
                {[['Services', '/services'], ['About', '/about'], ['Contact', '/contact'], ['Reviews', '/reviews']].map(([label, href]) => (
                  <Link key={href} to={href} className="text-secondary-foreground/70 hover:text-primary transition-colors">{label}</Link>
                ))}
              </nav>
              <div className="flex items-center gap-4 text-sm">
                <a href={`tel:${phone.replace(/\D/g, '')}`} className="flex items-center gap-1 hover:text-primary transition-colors">
                  <Phone className="h-4 w-4" /> {phone}
                </a>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-secondary-foreground/10 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-secondary-foreground/50">© {currentYear} {companyName}. All rights reserved.</p>
              <SocialLinks bizSettings={bizSettings} />
            </div>
          </div>
          <BrandingBar />
        </footer>
      );

    case 'centered':
      return (
        <footer className="bg-secondary text-secondary-foreground">
          <div className="container mx-auto px-4 py-12 text-center">
            <div className="flex items-center justify-center gap-3 mb-4" style={{ fontFamily: 'var(--cms-heading-font)' }}>
              {logoUrl ? (
                <img src={logoUrl} alt={companyName} className="h-10 w-auto object-contain" />
              ) : (
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: 'var(--cms-accent)' }}>
                  {companyName.charAt(0)}
                </div>
              )}
              <span className="font-bold text-xl">{companyName}</span>
            </div>
            <p className="text-sm text-secondary-foreground/70 max-w-md mx-auto mb-6">{ctx.template.brand?.tagline || ''}</p>
            <nav className="flex flex-wrap justify-center gap-6 text-sm mb-6">
              {[['Services', '/services'], ['Service Areas', '/service-areas'], ['About', '/about'], ['Contact', '/contact'], ['Careers', '/careers']].map(([label, href]) => (
                <Link key={href} to={href} className="text-secondary-foreground/70 hover:text-primary transition-colors">{label}</Link>
              ))}
            </nav>
            <div className="flex justify-center gap-4 mb-6 text-sm">
              <a href={`tel:${phone.replace(/\D/g, '')}`} className="flex items-center gap-1 hover:text-primary"><Phone className="h-4 w-4" /> {phone}</a>
              <a href={`mailto:${email}`} className="flex items-center gap-1 hover:text-primary"><Mail className="h-4 w-4" /> {email}</a>
            </div>
            <div className="flex justify-center mb-6">
              <LocationsList locations={locations} bizSettings={bizSettings} compact />
            </div>
            <div className="flex justify-center mb-6"><SocialLinks bizSettings={bizSettings} /></div>
            <div className="pt-6 border-t border-secondary-foreground/10">
              <p className="text-xs text-secondary-foreground/50">© {currentYear} {companyName}. All rights reserved.</p>
            </div>
          </div>
          <BrandingBar />
        </footer>
      );

    case 'split':
      return (
        <footer className="bg-secondary text-secondary-foreground">
          <div className="container mx-auto px-4 py-12">
            <div className="grid md:grid-cols-2 gap-12">
              {/* CTA side */}
              <div>
                <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'var(--cms-heading-font)' }}>Ready to get started?</h3>
                <p className="text-secondary-foreground/70 mb-6">Contact us today for a free consultation and estimate.</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button asChild style={{ background: 'var(--cms-accent)' }}>
                    <Link to="/contact" className="flex items-center gap-2">Get Free Estimate <ArrowRight className="h-4 w-4" /></Link>
                  </Button>
                  <Button asChild variant="outline" className="border-secondary-foreground/20 text-secondary-foreground hover:bg-secondary-foreground/10">
                    <a href={`tel:${phone.replace(/\D/g, '')}`} className="flex items-center gap-2"><Phone className="h-4 w-4" /> {phone}</a>
                  </Button>
                </div>
                <div className="mt-8"><SocialLinks bizSettings={bizSettings} /></div>
              </div>
              {/* Links side */}
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <h4 className="font-semibold text-sm uppercase tracking-wider mb-4 text-secondary-foreground/50">Company</h4>
                  <ul className="space-y-2 text-sm">
                    {[['About', '/about'], ['Services', '/services'], ['Reviews', '/reviews'], ['Careers', '/careers']].map(([label, href]) => (
                      <li key={href}><Link to={href} className="text-secondary-foreground/70 hover:text-primary transition-colors">{label}</Link></li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-sm uppercase tracking-wider mb-4 text-secondary-foreground/50">Contact</h4>
                  <div className="space-y-2 text-sm text-secondary-foreground/70">
                    <a href={`tel:${phone.replace(/\D/g, '')}`} className="flex items-center gap-2 hover:text-primary"><Phone className="h-3.5 w-3.5" /> {phone}</a>
                    <a href={`mailto:${email}`} className="flex items-center gap-2 hover:text-primary"><Mail className="h-3.5 w-3.5" /> {email}</a>
                    <LocationsList locations={locations} bizSettings={bizSettings} compact />
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-secondary-foreground/10 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-secondary-foreground/50">© {currentYear} {companyName}. All rights reserved.</p>
              <div className="flex items-center gap-4 text-xs text-secondary-foreground/50">
                <Link to="/sitemap" className="hover:text-primary">Sitemap</Link>
                <Link to="/privacy-policy" className="hover:text-primary">Privacy</Link>
                <Link to="/terms-of-service" className="hover:text-primary">Terms</Link>
              </div>
            </div>
          </div>
          <BrandingBar />
        </footer>
      );

    case 'mega':
    default:
      return (
        <footer className="bg-secondary text-secondary-foreground">
          <div className="container mx-auto px-4 py-12 lg:py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
              {/* Company Info */}
              <div>
                <Link to="/" className="flex items-center gap-2 mb-4" style={{ fontFamily: 'var(--cms-heading-font)' }}>
                  {logoUrl ? (
                    <img src={logoUrl} alt={companyName} className="h-10 w-auto object-contain" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: 'var(--cms-accent)' }}>
                      {companyName.charAt(0)}
                    </div>
                  )}
                  <span className="font-bold text-lg">{companyName}</span>
                </Link>
                <p className="text-sm text-secondary-foreground/80 mb-4">{ctx.template.brand?.tagline || ''}</p>
                <div className="space-y-2 text-sm">
                  <a href={`tel:${phone.replace(/\D/g, '')}`} className="flex items-center gap-2 hover:text-primary transition-colors"><Phone className="h-4 w-4" /> {phone}</a>
                  <a href={`mailto:${email}`} className="flex items-center gap-2 hover:text-primary transition-colors"><Mail className="h-4 w-4" /> {email}</a>
                  <LocationsList locations={locations} bizSettings={bizSettings} />
                  <div className="flex items-center gap-2"><Clock className="h-4 w-4" /> {hours}</div>
                </div>
              </div>
              {/* Services */}
              <div>
                <h4 className="font-semibold text-lg mb-4" style={{ fontFamily: 'var(--cms-heading-font)' }}>Services</h4>
                <ul className="space-y-2">
                  {ctx.services.slice(0, 6).map(s => (
                    <li key={s.slug}><Link to={`/services#${s.slug}`} className="text-sm text-secondary-foreground/80 hover:text-primary transition-colors flex items-center gap-2">
                      <DynamicIcon name={s.icon} className="h-3 w-3" /> {s.title}
                    </Link></li>
                  ))}
                  <li><Link to="/services" className="text-sm text-primary hover:underline">View All →</Link></li>
                </ul>
              </div>
              {/* Quick Links */}
              <div>
                <h4 className="font-semibold text-lg mb-4" style={{ fontFamily: 'var(--cms-heading-font)' }}>Quick Links</h4>
                <ul className="space-y-2">
                  {[['Our Services', '/services'], ['Service Areas', '/service-areas'], ['Get a Quote', '/contact'], ['About Us', '/about'], ['Resources', '/resources'], ['Contact', '/contact'], ['Careers', '/careers']].map(([label, href]) => (
                    <li key={label}><Link to={href} className="text-sm text-secondary-foreground/80 hover:text-primary transition-colors">{label}</Link></li>
                  ))}
                </ul>
              </div>
              {/* Newsletter */}
              <div>
                <h4 className="font-semibold text-lg mb-4" style={{ fontFamily: 'var(--cms-heading-font)' }}>Stay Updated</h4>
                <p className="text-sm text-secondary-foreground/80 mb-4">Get tips, deals, and news delivered to your inbox.</p>
                <NewsletterForm variant="stacked" source="footer" />
                <h4 className="font-semibold text-lg mt-6 mb-3" style={{ fontFamily: 'var(--cms-heading-font)' }}>Service Areas</h4>
                <ul className="space-y-1">
                  {ctx.serviceAreas.slice(0, 4).map(a => {
                    const name = typeof a === 'string' ? a : a.name;
                    return <li key={name}><Link to={`/service-areas#${name.toLowerCase().replace(/\s+/g, '-')}`} className="text-sm text-secondary-foreground/80 hover:text-primary transition-colors">{name}</Link></li>;
                  })}
                </ul>
              </div>
            </div>
            {/* Trust badges */}
            <div className="mt-8 pt-8 border-t border-secondary-foreground/10">
              <div className="flex flex-wrap justify-center items-center gap-6">
                {ctx.template.trustBadges?.map((badge: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-secondary-foreground/80">
                    <DynamicIcon name={badge.icon} className="h-5 w-5 text-primary" />
                    <span>{badge.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Bottom bar */}
          <div className="border-t border-secondary-foreground/10">
            <div className="container mx-auto px-4 py-4">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <p className="text-sm text-secondary-foreground">© {currentYear} {companyName}. All rights reserved.</p>
                <SocialLinks bizSettings={bizSettings} />
                <div className="flex items-center gap-4 text-sm text-secondary-foreground">
                  <Link to="/sitemap" className="hover:text-primary">Sitemap</Link>
                  <Link to="/privacy-policy" className="hover:text-primary">Privacy</Link>
                  <Link to="/terms-of-service" className="hover:text-primary">Terms</Link>
                </div>
              </div>
            </div>
          </div>
          <BrandingBar />
        </footer>
      );
  }
};
